/**
 * Utilities for checking authentication state in query hooks.
 * Centralizes the pattern of disabling queries when user is logged out.
 */

/**
 * Checks if user has a valid auth token for making authenticated requests.
 * Used to disable queries when user is logged out to prevent error banners.
 *
 * @param authToken - The current auth token or null
 * @returns true if user appears to be authenticated
 */
export function hasValidAuthToken(authToken: string | null | undefined): authToken is string {
  return typeof authToken === "string" && authToken.length > 0;
}

/**
 * Computes the enabled state for a query, combining auth check with consumer options.
 * Auth check is always applied - consumer cannot override to enable unauthenticated queries.
 *
 * @param authToken - The current auth token
 * @param consumerEnabled - The enabled option from consumer, if provided
 * @returns Combined enabled state
 */
export function computeQueryEnabled(
  authToken: string | null | undefined,
  consumerEnabled?: boolean,
): boolean {
  const hasAuth = hasValidAuthToken(authToken);

  // Debug logging to aid in troubleshooting query enable/disable behavior
  if (process.env.NODE_ENV === "development") {
    console.debug("[queryUtils] computeQueryEnabled:", {
      hasAuth,
      consumerEnabled,
      result: hasAuth && consumerEnabled !== false,
    });
  }

  // Auth check is mandatory - prevents errors during logout
  if (!hasAuth) {
    return false;
  }
  // Respect consumer's enabled option if explicitly set to false
  return consumerEnabled !== false;
}
